import * as dateFns from "date-fns";
import fi from "date-fns/locale/fi";
import { gql, useQuery } from "@apollo/client";
import PageHeader from "../theme/components/common/PageHeader";
import * as React from "react";
import { Link } from "react-router-dom";
import Table from "react-bootstrap/Table";
import Card from "react-bootstrap/Card";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { toast } from "react-toastify";
import debounce from "lodash/debounce";
import apiClient from "../api";
import { gettext } from "../i18n";
import SessionTimeout from "components/common/SessionTimeout";
import { formatNumberLocale } from "utils/formatting";
import { useModalContext, modalSetters } from "state/modal";
import {
  GET_ALL_STATIONS_FOR_ALARMS,
  GET_STATION_DATASOURCES_FOR_ALARMS,
} from "utils/alarms";
import {
  GetAllAutoFilters,
  GetAllAutoFilters_datapoint_deletion_request,
} from "pages/__generated__/GetAllAutoFilters";
import DataRemovalSettingForm from "components/modals/DataRemovalSettingForm";
import "./QualityAssurancePage.scss";

const GET_ALL_AUTO_FILTERS = gql`
  query GetAllAutoFilters($searchQuery: String) {
    datapoint_deletion_request(
      where: {
        auto_filter: { _eq: true }
        _or: [
          { station: { name: { _ilike: $searchQuery } } }
          { station: { identifier: { _ilike: $searchQuery } } }
          { station: { organization: { name: { _ilike: $searchQuery } } } }
        ]
      }
      order_by: [{ station: { name: asc } }]
    ) {
      id
      source_uuid
      lower_limit
      upper_limit
      range_from
      range_to
      select_spikes
      spike_threshold
      compare_readings
      hide
      auto_filter
      auto_filter_active
      timestamp
      station {
        id
        name
        identifier
        organization {
          name
        }
      }
      datasource {
        type
        name
        source_id
        unit {
          symbol
        }
      }
    }
  }
`;

const formatDate = (dateStr: string | null) => {
  if (!dateStr) return "-";
  try {
    return dateFns.format(new Date(dateStr), "dd.MM.yyyy HH:mm", {
      locale: fi,
    });
  } catch {
    return dateStr;
  }
};

const formatFilterCriteria = (
  filter: GetAllAutoFilters_datapoint_deletion_request,
) => {
  const parts: string[] = [];
  if (filter.lower_limit !== null && filter.lower_limit !== undefined) {
    parts.push(
      `${gettext("Lower")}: ${formatNumberLocale(filter.lower_limit)}`,
    );
  }
  if (filter.upper_limit !== null && filter.upper_limit !== undefined) {
    parts.push(
      `${gettext("Upper")}: ${formatNumberLocale(filter.upper_limit)}`,
    );
  }
  if (filter.select_spikes) {
    parts.push(gettext("Spikes"));
  }
  if (
    filter.compare_readings !== null &&
    filter.compare_readings !== undefined
  ) {
    parts.push(`${gettext("Compare")}: ${filter.compare_readings}`);
  }
  return parts.length > 0 ? parts.join(", ") : gettext("Range only");
};

interface AddFilterPickerProps {
  show: boolean;
  onHide: () => void;
  onSelect: (
    stationId: number,
    datasource: any,
    visibleDatasources: any,
  ) => void;
}

const AddFilterPicker = ({ show, onHide, onSelect }: AddFilterPickerProps) => {
  const [selectedStationId, setSelectedStationId] = React.useState<
    number | null
  >(null);
  const [selectedDsValue, setSelectedDsValue] = React.useState<string>("");

  const { data: stationsData } = useQuery(GET_ALL_STATIONS_FOR_ALARMS);
  const { data: dataSourcesData } = useQuery(
    GET_STATION_DATASOURCES_FOR_ALARMS,
    {
      variables: { stationId: selectedStationId! },
      skip: !selectedStationId,
    },
  );

  const handleStationChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedStationId(parseInt(e.target.value) || null);
    setSelectedDsValue("");
  };

  const handleConfirm = () => {
    if (!selectedStationId || !selectedDsValue) {
      return;
    }
    const ds = dataSourcesData?.station?.[0]?.datasources?.find(
      (d: any) => d.source_uuid === selectedDsValue,
    );
    if (ds) {
      onSelect(
        selectedStationId,
        ds,
        dataSourcesData?.station?.[0]?.datasources ?? [],
      );
    }
  };

  const handleClose = () => {
    setSelectedDsValue("");
    onHide();
  };

  const datasources = dataSourcesData?.station?.[0]?.datasources ?? [];
  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>{gettext("Add new filter")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label>{gettext("Station")}</Form.Label>
          <Form.Select
            value={selectedStationId ?? ""}
            onChange={handleStationChange}
          >
            <option value="">{gettext("Select station...")}</option>
            {stationsData?.station?.map((s: any) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>{gettext("Parameter")}</Form.Label>
          <Form.Select
            value={selectedDsValue}
            onChange={(e) => setSelectedDsValue(e.target.value)}
            disabled={!selectedStationId}
          >
            <option value="">{gettext("Select parameter...")}</option>
            {datasources.map((ds: any) => (
              <option key={`${ds.source_uuid}`} value={`${ds.source_uuid}`}>
                {ds.name}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={handleClose}>
          {gettext("Cancel")}
        </Button>
        <Button
          variant="primary"
          onClick={handleConfirm}
          disabled={!selectedStationId || !selectedDsValue}
        >
          {gettext("Next")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

interface FiltersTableProps {
  filters: GetAllAutoFilters_datapoint_deletion_request[];
  onSuccess: () => void;
  onAddFilter: () => void;
}

interface FilterRowProps {
  filter: GetAllAutoFilters_datapoint_deletion_request;
  onSuccess: () => void;
}

const FilterRow = ({ filter, onSuccess }: FilterRowProps) => {
  const handleToggle = (activate: boolean) => {
    if (
      !activate &&
      !window.confirm(
        gettext(
          "Are you sure you want to deactivate this auto-filter? Previously hidden data will remain hidden.",
        ),
      )
    ) {
      return;
    }
    apiClient
      .request("/organization-admin/data-filtering/toggle-auto-filter", {
        method: "POST",
        data: { id: filter.id, activate },
      })
      .then(() => {
        const message = activate
          ? gettext("Auto-filter activated successfully")
          : gettext("Auto-filter deactivated successfully");
        toast.success(message);
        onSuccess();
      })
      .catch((e) => {
        console.error(e);
        const errorMessage = activate
          ? gettext("Error activating auto-filter")
          : gettext("Error deactivating auto-filter");
        toast.error(
          `${errorMessage}: ${e.message?.toString() || "Unknown error"}`,
        );
      });
  };

  const handleRemove = () => {
    const isHideFilter = filter.hide;
    const confirmMessage = isHideFilter
      ? gettext(
          "Are you sure you want to remove this auto-filter? This will also restore (unhide) previously hidden datapoints.",
        )
      : gettext(
          "Are you sure you want to remove this auto-filter? Deleted datapoints cannot be restored.",
        );
    if (!window.confirm(confirmMessage)) {
      return;
    }
    apiClient
      .request("/organization-admin/data-filtering/remove-auto-filter", {
        method: "POST",
        data: { id: filter.id },
      })
      .then(() => {
        toast.success(gettext("Auto-filter removed successfully"));
        onSuccess();
      })
      .catch((e) => {
        console.error(e);
        toast.error(
          `${gettext("Error removing auto-filter")}: ${e.message?.toString() || "Unknown error"}`,
        );
      });
  };

  const isActive = filter.auto_filter_active;

  return (
    <tr key={filter.id} className={isActive ? "" : "table-secondary"}>
      <td data-label={gettext("Organization")}>
        {filter.station?.organization?.name ?? "—"}
      </td>
      <td data-label={gettext("Station")}>
        {filter.station ? (
          <Link to={`/stations/${filter.station.identifier}`}>
            {filter.station.name}
          </Link>
        ) : (
          "-"
        )}
      </td>
      <td data-label={gettext("Parameter")}>
        {filter.datasource?.name ?? "-"}
        {filter.datasource?.unit?.symbol
          ? ` (${filter.datasource.unit.symbol})`
          : ""}
      </td>
      <td data-label={gettext("Date range")}>
        {formatDate(filter.range_from)} - {formatDate(filter.range_to)}
      </td>
      <td data-label={gettext("Filter criteria")}>
        {formatFilterCriteria(filter)}
      </td>
      <td data-label={gettext("Actions")}>
        <div className="d-flex flex-wrap gap-1">
          {isActive ? (
            <>
              <Button
                variant="outline-warning"
                size="sm"
                onClick={() => handleToggle(false)}
              >
                {gettext("Deactivate")}
              </Button>
              <Button variant="outline-danger" size="sm" onClick={handleRemove}>
                {gettext("Remove")}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline-success"
                size="sm"
                onClick={() => handleToggle(true)}
              >
                {gettext("Activate")}
              </Button>
              <Button variant="outline-danger" size="sm" onClick={handleRemove}>
                {gettext("Remove")}
              </Button>
            </>
          )}
        </div>
      </td>
    </tr>
  );
};

const FiltersTable = ({
  filters,
  onSuccess,
  onAddFilter,
}: FiltersTableProps) => {
  const activeFilters = filters.filter((f) => f.auto_filter_active);
  const inactiveFilters = filters.filter((f) => !f.auto_filter_active);

  return (
    <div className="qa-filters-section">
      <h5 className="mb-3">{gettext("Active Filters")}</h5>
      <div className="overflow-auto">
        <Table style={{ tableLayout: "auto" }} className="mb-4">
          <thead>
            <tr>
              <th style={{ width: "15%" }}>{gettext("Organization")}</th>
              <th style={{ width: "20%" }}>{gettext("Station")}</th>
              <th style={{ width: "20%" }}>{gettext("Parameter")}</th>
              <th style={{ width: "20%" }}>{gettext("Date range")}</th>
              <th style={{ width: "15%" }}>{gettext("Filter criteria")}</th>
              <th style={{ width: "auto" }}>{gettext("Actions")}</th>
            </tr>
          </thead>
          <tbody>
            {activeFilters.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center">
                  {gettext("No active auto-filters")}
                </td>
              </tr>
            ) : (
              activeFilters.map((filter) => (
                <FilterRow
                  key={filter.id}
                  filter={filter}
                  onSuccess={onSuccess}
                />
              ))
            )}
          </tbody>
        </Table>
      </div>

      {inactiveFilters.length > 0 && (
        <div className="qa-inactive-filters">
          <h5 className="mb-3">{gettext("Inactive Filters")}</h5>
          <div className="overflow-auto">
            <Table style={{ tableLayout: "auto" }}>
              <thead>
                <tr>
                  <th style={{ width: "15%" }}>{gettext("Organization")}</th>
                  <th style={{ width: "20%" }}>{gettext("Station")}</th>
                  <th style={{ width: "20%" }}>{gettext("Parameter")}</th>
                  <th style={{ width: "20%" }}>{gettext("Date range")}</th>
                  <th style={{ width: "15%" }}>{gettext("Filter criteria")}</th>
                  <th style={{ width: "auto" }}>{gettext("Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {inactiveFilters.map((filter) => (
                  <FilterRow
                    key={filter.id}
                    filter={filter}
                    onSuccess={onSuccess}
                  />
                ))}
              </tbody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
};

const QualityAssurancePage = () => {
  const [stationQuery, setStationQuery] = React.useState("");
  const [debouncedStationQuery, setDebouncedStationQuery] = React.useState("");
  const debouncedSetQuery = React.useMemo(
    () => debounce((value: string) => setDebouncedStationQuery(value), 400),
    [],
  );

  React.useEffect(() => {
    debouncedSetQuery(stationQuery);
    return () => debouncedSetQuery.cancel();
  }, [stationQuery, debouncedSetQuery]);

  const { data, error, refetch } = useQuery<GetAllAutoFilters>(
    GET_ALL_AUTO_FILTERS,
    {
      variables: { searchQuery: `%${debouncedStationQuery}%` },
    },
  );
  const [showPicker, setShowPicker] = React.useState(false);
  const [selectedStationId, setSelectedStationId] = React.useState<
    number | null
  >(null);
  const [visibleDatasources, setVisibleDatasources] = React.useState([]);
  const {
    activeModal,
    selectedDataSource,
    setActiveModal,
    setSelectedDataSource,
  } = useModalContext();

  const handlePickerSelect = React.useCallback(
    (_stationId: number, _datasource: any, _visibleDatasources: any) => {
      setSelectedStationId(_stationId);
      setSelectedDataSource?.(_datasource);
      setActiveModal?.("dataRemoval");
      setVisibleDatasources(_visibleDatasources);
      setShowPicker(false);
    },
    [setSelectedDataSource, setActiveModal],
  );

  if (error) {
    console.error("QualityAssurancePage GQL error: ", error);
    return <SessionTimeout />;
  }

  const filters = data?.datapoint_deletion_request ?? [];

  const handleAddFilter = () => {
    setShowPicker(true);
  };

  return (
    <>
      <PageHeader
        title={gettext("Quality Assurance")}
        description={gettext(
          "Manage active auto-filters across all stations and parameters",
        )}
      >
        <Button variant="primary" size="sm" onClick={handleAddFilter}>
          {gettext("Add new filter")}
        </Button>
      </PageHeader>
      <Card className="mb-3">
        <Card.Body>
          <Row className="g-3 mb-3">
            <Col xs={12} lg={6}>
              <Form.Label className="fw-semibold">Station</Form.Label>
              <Form.Control
                type="text"
                placeholder={gettext(
                  "Search by station name, identifier or organization",
                )}
                value={stationQuery}
                onChange={(e) => setStationQuery(e.target.value)}
              />
            </Col>
          </Row>
          <FiltersTable
            filters={filters}
            onSuccess={() => refetch()}
            onAddFilter={handleAddFilter}
          />
        </Card.Body>
      </Card>
      <AddFilterPicker
        show={showPicker}
        onHide={() => setShowPicker(false)}
        onSelect={handlePickerSelect}
      />
      {(!!selectedDataSource || !!visibleDatasources.length) &&
        selectedStationId &&
        activeModal === "dataRemoval" && (
          <DataRemovalSettingForm
            autoFilterOnly
            stationId={selectedStationId}
            visibleSources={visibleDatasources}
          />
        )}
    </>
  );
};

export default QualityAssurancePage;
